import { Button, Callout, IconButton, Tag, Toast } from '@dev-spendesk/grapes';
import { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';

import { useParams } from 'src/core/common/hooks/useParams';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { routeFor, routes } from 'src/core/constants/routes';
import {
  NotificationType,
  useNotifications,
} from 'src/core/modules/app/notifications';
import wiseLogo from 'src/core/modules/integrations/assets/integration-logos/wise.svg';
import { useGetWiseKycStatus } from 'src/core/modules/invoices/transfer';
import { useUpdateKycReview } from 'src/core/modules/invoices/transfer/hooks/api/useUpdateKycReview';
import { AnalyticEventName, track } from 'src/core/utils/analytics';

import { WiseConnectionModal } from '../../modules/integrations/settings/components/ConnectionModal/WiseConnectionModal';

const KYC_STATUSES_TO_DISPLAY_BUTTON = ['NEW', 'WAITING_CUSTOMER_INPUT'];

export const WiseIntegrationLandingPage = () => {
  const { t } = useTranslation('global');

  const { company: companyId } = useParams(
    routes.WISE_INTEGRATION_LANDING.path,
  );

  const { pushNotif } = useNotifications();

  const history = useHistory();

  const [displayConnectionModal, setDisplayConnectionModal] = useState(false);

  const { data: kycStatus } = useGetWiseKycStatus({
    companyId,
    enabled: true,
  });

  const baseUrl = `${window.location.protocol}//${window.location.host}`;

  const { mutateAsync: createKycReview } = useUpdateKycReview({
    companyId,
    redirectUserTo: `${baseUrl}/app/${companyId}/company/integrations/all/wise?status=success`,
  });

  const [redirectSuccess, setRedirectSuccess] = useState(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get('status') === 'success') {
      track(AnalyticEventName.INTEGRATIONS_WISE_KYB_SUBMITTED, {});
      setRedirectSuccess(true);
    }
  }, [window.location.search]);

  const handleGoBack = () => {
    history.push(
      routeFor(routes.COMPANY_INTEGRATIONS_ALL.path, {
        company: companyId,
      }),
    );
  };

  const handleGoToWiseButton = async () => {
    try {
      const { redirectUrl } = await createKycReview();

      window.open(redirectUrl, '_blank');
    } catch {
      pushNotif({
        type: NotificationType.Danger,
        message: t('wiseIntegrationLandingPage.errorNotificationRedirectTitle'),
      });
    }
  };

  const handleCloseModalButton = () => {
    setDisplayConnectionModal(false);
  };

  const handleOpenModalButton = () => {
    setDisplayConnectionModal(true);
  };

  return (
    <div className="flex max-w-lg flex-col items-start gap-24 p-40">
      <h1 className="text-content-primary heading-xl">
        {t('wiseIntegrationLandingPage.title')}
      </h1>

      <div className="separator" />

      <IconButton
        className="self-start"
        iconName="arrow-left"
        variant="secondaryNeutral"
        onClick={handleGoBack}
        aria-label="Go Back"
      />

      <div className="flex items-center gap-16">
        <img src={wiseLogo} alt="wise-logo" className="h-56 w-56" />

        <h2 className="font-medium">Wise</h2>

        {kycStatus === 'PASSED' ? (
          <Tag iconName="circle-check" variant="success">
            {t('wiseIntegrationLandingPage.successTag')}
          </Tag>
        ) : (
          <Tag iconName="circle-information" variant="info">
            {t('wiseIntegrationLandingPage.infoTag')}
          </Tag>
        )}
      </div>

      <p className="text-content-secondary-bg-secondary">
        {t('wiseIntegrationLandingPage.description')}
      </p>

      {
        /* Check if it's not coming from a success redirect, otherwise 
      this will show while the KYC status is being updated in the background */
        KYC_STATUSES_TO_DISPLAY_BUTTON.includes(kycStatus ?? '') &&
          !redirectSuccess && (
            <Callout
              className="w-full"
              title="Review your profile creation in Wise"
              variant="info"
            >
              <Button onClick={handleGoToWiseButton} text="Go to Wise" />
            </Callout>
          )
      }

      {!kycStatus ? (
        <Button
          onClick={handleOpenModalButton}
          text={t('connectToWiseModal.secondButtonText')}
        />
      ) : null}

      {displayConnectionModal ? (
        <WiseConnectionModal handleCloseModalButton={handleCloseModalButton} />
      ) : null}

      <div className="separator" />

      <div className="flex flex-col gap-16">
        <h3 className="font-semibold">Company information</h3>

        <div className="text-content-secondary-bg-secondary font-medium">
          <Trans i18nKey="wiseIntegrationLandingPage.companyInformation">
            <p>-</p>
            <p>-</p>
            <p className="">
              -
              <a
                href="https://register.fca.org.uk/s/firm?id=001b000001EjC6SAAV"
                target="blank"
                rel="noopener noreferrer"
              >
                -
              </a>
              -
            </p>
          </Trans>
        </div>
      </div>

      <div className="separator" />

      {redirectSuccess && (
        <Toast
          className="absolute bottom-32"
          title={t('wiseIntegrationLandingPage.backToInvoicesToast.title')}
          variant="success"
          onClose={() => setRedirectSuccess(false)}
        >
          <Link
            to={routeFor(routes.INVOICES_CONFIRM.path, { company: companyId })}
          >
            <p style={{ color: 'white' }} className="mt-8">
              {t('wiseIntegrationLandingPage.backToInvoicesToast.description')}
            </p>
          </Link>
        </Toast>
      )}
    </div>
  );
};
